import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Button, Card, Cluster, FormField, Input, Modal, Stack, guardSubmit } from "@salesapp/ui";
import { TipHeading } from "./AdminTip";
import { api } from "./api";
import { notifySaved } from "./savedToast";
import { formatUtc } from "./format";
import { Flash } from "./Flash";

interface AllowedIp {
  id: string;
  cidr: string;
  label: string;
  created_at: string;
}

interface Overview {
  active: boolean;
  message: string;
  started_at: string | null;
  allowed_ips: AllowedIp[];
  your_ip: string;
  live_customer_sessions: number;
}

export function MaintenancePage() {
  const [data, setData] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [cidr, setCidr] = useState("");
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  async function load() {
    try {
      setData(await api.get<Overview>("/operator/maintenance"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load maintenance settings.");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function run(action: () => Promise<void>) {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await action();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "That did not work. Try again.");
    } finally {
      setBusy(false);
    }
  }

  function addIp(event: FormEvent) {
    event.preventDefault();
    return run(async () => {
      await api.post("/operator/maintenance/allowed-ips", { cidr, label });
      setCidr("");
      setLabel("");
      notifySaved("Allowed address saved.");
    });
  }

  function removeIp(row: AllowedIp) {
    void run(async () => {
      await api.delete(`/operator/maintenance/allowed-ips/${row.id}`);
    });
  }

  function disconnectAll() {
    setConfirmOpen(false);
    void run(async () => {
      const res = await api.post<{ sessions_revoked: number }>("/operator/maintenance/disconnect-users", {
        confirm: true,
      });
      setNotice(`Signed out ${res.sessions_revoked} customer ${res.sessions_revoked === 1 ? "session" : "sessions"}.`);
    });
  }

  if (!data) {
    return error ? <p className="admin-error">{error}</p> : <p>Loading…</p>;
  }

  const yourIpListed = data.allowed_ips.some((row) => row.cidr === `${data.your_ip}/32` || row.cidr === `${data.your_ip}/128`);

  return (
    <Stack gap={4}>
      <TipHeading
        as="h2"
        label="Maintenance"
        tip="Planned outages. The maintenance page is switched on and off from the maintenance_on / maintenance_off scripts in the scripts folder; this page manages who may still get in (the allowlist) and signs customers out beforehand."
      />
      {error && <Flash tone="error">{error}</Flash>}
      {notice && <Flash tone="success">{notice}</Flash>}

      <Card>
        <Stack gap={2}>
          <h3>Status</h3>
          <p>
            <strong>{data.active ? "Maintenance is ON" : "Maintenance is off"}</strong>
            {data.active && data.started_at ? ` since ${formatUtc(data.started_at)}` : ""}
          </p>
          <p className="admin-muted">
            Customers see: {data.message}
          </p>
        </Stack>
      </Card>

      <Card>
        <Stack gap={3}>
          <h3>Allowed addresses</h3>
          <p className="admin-muted">
            These addresses keep full access while maintenance is on, for post-production testing. Changes reach the
            edge the next time maintenance_on is run.
          </p>
          <table>
            <thead>
              <tr>
                <th>Address</th>
                <th>Label</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.allowed_ips.map((row) => (
                <tr key={row.id}>
                  <td><code>{row.cidr}</code></td>
                  <td>{row.label}</td>
                  <td className="admin-readonly">{formatUtc(row.created_at)}</td>
                  <td>
                    <Button type="button" variant="secondary" disabled={busy} onClick={() => removeIp(row)}>
                      Remove
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.allowed_ips.length === 0 && (
            <p className="admin-muted">No addresses yet. Add yours before switching maintenance on.</p>
          )}
          <form onSubmit={guardSubmit(addIp)}>
            <Stack gap={3}>
              <Cluster gap={3} align="end">
                <FormField label="IP address or range" htmlFor="maint-cidr" hint="For example 203.0.113.7 or 41.10.0.0/16">
                  <Input id="maint-cidr" value={cidr} onChange={(e) => setCidr(e.target.value)} required />
                </FormField>
                <FormField label="Label" htmlFor="maint-label" hint="Who is this, for example South Africa QA team">
                  <Input id="maint-label" value={label} onChange={(e) => setLabel(e.target.value)} required />
                </FormField>
                <Button type="submit" disabled={busy}>Add address</Button>
              </Cluster>
              <Cluster gap={3} align="center">
                <span className="admin-muted">
                  Your current address is <code>{data.your_ip}</code>
                  {yourIpListed ? " (already allowed)" : ""}
                </span>
                {!yourIpListed && (
                  <Button type="button" variant="secondary" disabled={busy} onClick={() => setCidr(data.your_ip)}>
                    Use my address
                  </Button>
                )}
              </Cluster>
            </Stack>
          </form>
        </Stack>
      </Card>

      <Card>
        <Stack gap={3}>
          <h3>Sign customers out</h3>
          <p className="admin-muted">
            Ends every customer session so nobody is mid-edit when the outage starts. Platform staff sessions are not
            affected. Send the outage notice email first; customers who return will have to sign in again.
          </p>
          <p>
            <strong>{data.live_customer_sessions}</strong> customer{" "}
            {data.live_customer_sessions === 1 ? "session is" : "sessions are"} currently signed in.
          </p>
          <div>
            <Button type="button" variant="danger" disabled={busy} onClick={() => setConfirmOpen(true)}>
              Disconnect all customers
            </Button>
          </div>
        </Stack>
      </Card>

      <Modal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Disconnect all customers"
        footer={(
          <>
            <Button type="button" variant="secondary" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button type="button" variant="danger" onClick={disconnectAll}>
              Sign out {data.live_customer_sessions} {data.live_customer_sessions === 1 ? "session" : "sessions"}
            </Button>
          </>
        )}
      >
        <p>
          Every signed-in customer is signed out immediately and unsaved work in their open tabs can be lost. Platform
          staff are not affected.
        </p>
      </Modal>
    </Stack>
  );
}
